<template>
  <div>
    <h2 v-if="showTitle" class="title">General info</h2>
    <v-layout class="pb-16">
      <v-flex class="pr-30" xs12>
        <select-field
          v-model="form.eventCategoryId"
          :validator="vs.eventCategoryId"
          :items="categories"
          item-select="id"
          item-render="name"
          label="Category*"
        />
      </v-flex>
      <v-flex xs12>
        <select-field
          v-model="form.interests"
          :items="getSubCategories"
          :validator="vs.interests"
          item-select="id"
          item-render="name"
          :disabled="!form.eventCategoryId || !getSubCategories.length"
          label="Subcategory*"
          multiple
        />
      </v-flex>
    </v-layout>
    <select-field
      v-model.number="form.genreId"
      :items="allGenres"
      label="Genre*"
      item-select="id"
      item-render="name"
      :validator="vs.genreId"
    />
    <text-field
      v-model.trim="form.title"
      label="Event title*"
      :error-messages="{ maxLength: 'Title is limited to 120 characters.' }"
      autofocus
      :validator="vs.title"
    />
    <div class="input-container">
      <text-field
        v-model.number="form.durationInMins"
        label="Duration"
        type="number"
        step="1"
        suffix="min"
        :validator="vs.durationInMins"
      />
      <checkbox v-model="form.break" color="#0277BD" style="margin-bottom: 1rem">
        <div>With a break</div>
      </checkbox>
    </div>
    <cover-field id="event-cover-field" v-model="form.cover" />
    <text-editor-field v-model="form.description" placeholder="Description" />
    <p class="text-field-description">Description will be visible on the event page</p>
    <select-field-multiple
      v-model="form.tagIds"
      :items="allTags"
      label="Tags"
      item-render="name"
      item-select="id"
    />
    <text-field
      v-model.trim="form.webLink"
      :validator="vs.webLink"
      label="Webpage or social media link"
      type="text"
    />
    <div class="input-container">
      <text-field
        v-model.trim="form.facebookLink"
        :validator="vs.facebookLink"
        label="Facebook link"
        type="text"
      />
      <text-field
        v-model.trim="form.instagramLink"
        :validator="vs.instagramLink"
        label="Instagram link"
        type="text"
      />
    </div>
    <div class="buttons-wrapper">
      <main-button class="back-btn" flat :text="'CANCEL'" @click="goBack()" />
      <div v-if="!showUpdateButton" class="buttons">
        <main-button class="continue-btn" depressed @click="() => this.$emit('next')">
          <span>continue</span>
          <v-icon class="arrow">arrow_forward</v-icon>
        </main-button>
      </div>
      <div v-else-if="enablePublishing">
        <v-tooltip top color="rgba(51, 51, 51, 0.80)" max-width="10rem">
          Save your progress without publishing the changes.
          <main-button
            slot="activator"
            text="Save draft"
            outline
            @click="$emit('update')"
          />
        </v-tooltip>
        <v-tooltip top color="rgba(51, 51, 51, 0.80)" max-width="10rem">
          Apply and make the changes visible to users.
          <main-button
            slot="activator"
            text="Publish changes"
            @click="$emit('update', true)"
          />
        </v-tooltip>
      </div>
      <main-button v-else text="UPDATE" @click="$emit('update')" />
    </div>
  </div>
</template>

<script>
import { GET_ORGANIZATION_INTEREST } from '@/graphql/interest.graphql'

export default {
  name: 'theater-event-info-form',
  props: {
    initialValues: {
      type: Object,
      default: () => {},
    },
    vs: {
      type: Object,
      default: () => {},
    },
    organizationSlug: String,
    showUpdateButton: { type: Boolean, default: false },
    showTitle: { type: Boolean, default: true },
    categories: {
      type: Array,
      default: () => [],
    },
    enablePublishing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: this.initialValues,
      allTags: [],
      allGenres: [],
    }
  },
  computed: {
    getSubCategories() {
      const selectedCategory = this.categories.find(c => c.id === this.form.eventCategoryId)

      if (!selectedCategory) {
        return []
      }

      return [...(selectedCategory.subCategories || [])].filter(sc => !sc.organizationId)
    },
  },
  watch: {
    'form.eventCategoryId': function (newVal, oldVal) {
      if (newVal != oldVal) {
        this.form.interests = []
      }
    },
  },
  mounted() {
    // Bullshit conversion for the select to work...
    if (this.form.eventCategoryId) {
      this.form.eventCategoryId = this.form.eventCategoryId.toString()
    }

    const targetId = window.location.hash
    if (targetId) {
      this.scrollToElement(targetId)
    }
  },
  methods: {
    scrollToElement(id) {
      const target = document.querySelector(id)
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
  },
  apollo: {
    getOrganizationInterest: {
      query: GET_ORGANIZATION_INTEREST,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
        }
      },
      result({ data, loading }) {
        if (!loading && data) {
          this.allGenres = data.getOrganizationInterest
            .filter(category => category.type === 'GENRE')
            .map(genre => {
              const { id, ...rest } = genre

              return { ...rest, id: parseInt(id) }
            })
          this.allTags = data.getOrganizationInterest
            .filter(category => category.type === 'TAG')
            .map(tag => {
              const { id, ...rest } = tag

              return { ...rest, id: parseInt(id) }
            })
        }
      },
    },
  },
}
</script>

<style>
.title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}

.buttons-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

@media screen and (max-width: 444px) {
  .buttons-wrapper {
    flex-direction: column-reverse;
  }

  .buttons {
    display: flex;
    flex-direction: column-reverse;
  }
}

.text-field-description {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #666666;
  margin-bottom: 1.5rem;
  margin-top: -1.875rem;
  margin-left: 0.875rem;
}

.arrow {
  margin-left: 0.625rem;
}

.input-container {
  display: flex;
  align-items: center;
  width: 100%;
  column-gap: 1.5rem;
  justify-content: space-between;
}
</style>
